import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import classnames from 'classnames';
import { makeStyles } from 'design-system/styles';
import { Typography } from 'react-scale/components/design-system/product';

type StyleProps = { isDragActive: boolean };
type Props = React.PropsWithChildren<{
  allowedMimeTypes: string;
  onFiles: (file: File[]) => void;
  uploadedFileName?: string;
  containerClassName?: string;
}>;

const FilesUpload: React.FC<Props> = ({
  allowedMimeTypes,
  onFiles,
  uploadedFileName,
  containerClassName,
  children,
}) => {
  const onDrop = useCallback(
    (files: File[]) => {
      if (files && files.length > 0) {
        onFiles(files);
      }
    },
    [onFiles],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  const classes = useStyles({ isDragActive });

  return (
    <div className={containerClassName}>
      <div {...getRootProps()} className={classnames(classes.dropzone, 'bg-neutral-100')}>
        <input type="file" {...getInputProps()} accept={allowedMimeTypes} />
        <img src="/static/img/product/upload-cyclops.svg" alt="" />
        <div className={classes.desc} style={{ whiteSpace: 'pre-wrap' }}>
          {uploadedFileName ? (
            <Typography>
              <span className="font-bold">{uploadedFileName}</span> uploaded! (click to re-upload)
            </Typography>
          ) : (
            <Typography>Drag and drop here or click to upload your data</Typography>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};

const useStyles = makeStyles<StyleProps>(theme => ({
  dropzone: props => ({
    width: '100%',
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: theme.borderRadius,
    border: props.isDragActive
      ? `1px dashed ${theme.colors.primary1}`
      : '1px solid rgba(0, 0, 0, 0)',
    cursor: 'pointer',
    transition: 'all 0.2s',

    '&:hover': {
      border: `1px dashed ${theme.colors.primary1}`,
    },
    padding: theme.spacing(0.5),
  }),
  desc: {
    marginTop: theme.spacing(3),
    color: theme.colors.gray50,
  },
}));

export default FilesUpload;
